import React, { useState } from 'react';
import { X, Upload, FileSpreadsheet, Calendar, Package, AlertCircle, CheckCircle2, Copy, Plus } from 'lucide-react';
import api from '../../utils/api';
import ConfigSectionShell from '../common/ConfigSectionShell';

interface TomaInfo {
  tomaNumber: number;
  fecha: string;
  clientId: string;
  recordCount: number;
  exists: boolean;
  selected: boolean;
}

interface ImportTomasModalProps {
  client: {
    uid: string;
    name: string;
    display_name?: string;
  };
  onClose: () => void;
  onImportComplete: () => void;
  inline?: boolean;
}

const ImportTomasModal: React.FC<ImportTomasModalProps> = ({ client, onClose, onImportComplete, inline = false }) => {
  const [file, setFile] = useState<File | null>(null);
  const [tomas, setTomas] = useState<TomaInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<any>(null);
  const [showErrorDetails, setShowErrorDetails] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [step, setStep] = useState<'upload' | 'select' | 'importing' | 'complete'>('upload');
  const [importProgress, setImportProgress] = useState({ 
    current: 0, 
    total: 0,
    currentToma: '',
    recordsProcessed: 0,
    totalRecords: 0
  });

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.xlsx') && !selectedFile.name.endsWith('.xls')) {
        setError('Por favor seleccione un archivo Excel (.xlsx o .xls)');
        return;
      }
      setFile(selectedFile);
      setError(null);
    }
  };

  const handleAnalyze = async () => {
    if (!file) return;

    setLoading(true);
    setError(null);

    try {
      // Convert file to base64
      const reader = new FileReader();
      const base64Promise = new Promise<string>((resolve, reject) => {
        reader.onload = () => {
          const base64 = (reader.result as string).split(',')[1];
          resolve(base64);
        };
        reader.onerror = (error) => {
          console.error('❌ ANALYZE - FileReader error:', error);
          reject(error);
        };
      });
      reader.readAsDataURL(file);

      const base64Data = await base64Promise;

      const response = await api.post(
        '/api/tables/analyze-excel-base64',
        {
          file: base64Data,
          fileName: file.name,
          clientId: client.uid
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'x-client-id': client.uid,
            'x-client-name': client.name
          },
          timeout: 60000 // 1 minuto de timeout para análisis
        }
      );

      const availableTomas = response.data.tomas.map((toma: any) => ({
        ...toma,
        selected: !toma.exists
      }));

      setTomas(availableTomas);
      setStep('select');
    } catch (err: any) {
      console.error('❌ ANALYZE - Error caught:', err);
      console.error('   Error message:', err.message);
      console.error('   Error response:', err.response);
      console.error('   Error stack:', err.stack);

      setError(err.response?.data?.error || 'Error al analizar el archivo Excel');
      setErrorDetails({
        message: err.message,
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        timestamp: new Date().toISOString()
      });
      console.error('Error analyzing Excel:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTomaToggle = (index: number) => {
    setTomas(prev => {
      const updated = prev.map((toma, i) => {
        if (i === index && !toma.exists) {
          return { ...toma, selected: !toma.selected };
        }
        return toma;
      });
      return updated;
    });
  };

  const handleSelectAll = () => {
    setTomas(prev => prev.map(toma => ({
      ...toma,
      selected: !toma.exists // Solo seleccionar las que no existen
    })));
  };

  const handleDeselectAll = () => {
    setTomas(prev => prev.map(toma => ({
      ...toma,
      selected: false
    })));
  };

  const handleImport = async () => {
    const selectedTomas = tomas.filter(t => t.selected && !t.exists);
    if (selectedTomas.length === 0) {
      setError('Por favor seleccione al menos una toma para importar');
      return;
    }

    setImporting(true);
    setError(null);
    setStep('importing');
    
    // Calcular el total de registros a importar
    const totalRecords = selectedTomas.reduce((sum, toma) => sum + (toma.recordCount || 0), 0);
    
    setImportProgress({ 
      current: 0, 
      total: selectedTomas.length,
      currentToma: '',
      recordsProcessed: 0,
      totalRecords: totalRecords
    });

    // Convert file to base64
    const reader = new FileReader();
    const base64Promise = new Promise<string>((resolve, reject) => {
      reader.onload = () => {
        const base64 = (reader.result as string).split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
    });
    reader.readAsDataURL(file!);

    const base64Data = await base64Promise;

    // Iniciar simulación de progreso gradual
    const estimatedTimePerRecord = 10; // ms por registro
    const estimatedTotalTime = totalRecords * estimatedTimePerRecord;
    const updateInterval = 500; // Actualizar cada 500ms
    const recordsPerUpdate = Math.ceil(totalRecords / (estimatedTotalTime / updateInterval));

    let simulatedRecords = 0;

    const progressInterval = setInterval(() => {
      const currentRecords = simulatedRecords; // Captura local para evitar warning de closure
      if (currentRecords < totalRecords * 0.95) { // Simular hasta 95%
        simulatedRecords = Math.min(currentRecords + recordsPerUpdate, totalRecords * 0.95);

        // Calcular qué toma estamos procesando
        let accumRecords = 0;
        for (let i = 0; i < selectedTomas.length; i++) {
          accumRecords += selectedTomas[i].recordCount || 0;
          if (simulatedRecords <= accumRecords) {
            setImportProgress(prev => ({
              ...prev,
              current: Math.min(i, selectedTomas.length - 1),
              currentToma: `Toma ${selectedTomas[i].tomaNumber}`,
              recordsProcessed: Math.floor(simulatedRecords)
            }));
            break;
          }
        }
      }
    }, updateInterval);

    try {

      const response = await api.post(
        '/api/tables/import-tomas-base64',
        {
          file: base64Data,
          fileName: file!.name,
          clientId: client.uid,
          tomasToImport: selectedTomas.map(t => t.tomaNumber)
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'x-client-id': client.uid,
            'x-client-name': client.name
          },
          timeout: 900000 // 15 minutos para importaciones muy grandes
        }
      );

      
      // Detener la simulación
      clearInterval(progressInterval);

      // Actualizar con el progreso final real del servidor
      if (response.data.summary) {
        setImportProgress(prev => ({ 
          ...prev, 
          current: response.data.imported,
          recordsProcessed: response.data.summary.totalRecordsImported
        }));
      }

      // Verificar si se importaron todos los registros
      const expectedRecords = response.data.summary?.totalRecordsExpected || totalRecords;
      const importedRecords = response.data.summary?.totalRecordsImported || 0;
      const failedRecords = expectedRecords - importedRecords;
      
      // Buscar detalles de errores en los resultados
      const resultsWithErrors = response.data.results?.filter((r: any) => 
        r.partial || (!r.success && r.recordsFailed > 0)
      ) || [];
      
      if (failedRecords > 0) {
        // HAY REGISTROS FALLIDOS - No cerrar automáticamente
        
        // Preparar detalles del error con la nueva información del servidor
        const errorSummary = {
          totalFailed: failedRecords,
          tomasAffected: resultsWithErrors.length,
          possibleCauses: response.data.summary?.possibleCauses || [],
          commonErrors: response.data.summary?.commonErrors || [],
          details: resultsWithErrors.map((r: any) => ({
            toma: r.tomaNumber,
            failed: r.recordsFailed,
            errors: r.errorDetails?.firstErrors || []
          }))
        };
        
        // Solo establecer el error principal para el título
        setError(`⚠️ Importación incompleta: ${failedRecords.toLocaleString('es-ES')} registros no importados`);
        
        setErrorDetails({
          message: `${importedRecords.toLocaleString('es-ES')} de ${expectedRecords.toLocaleString('es-ES')} registros procesados`,
          summary: errorSummary,
          timestamp: new Date().toISOString()
        });
        
        // Mensaje de resumen más claro
        const tomaText = response.data.imported === 1 ? 'toma' : 'tomas';
        setSuccess(`${response.data.imported} ${tomaText} procesada${response.data.imported !== 1 ? 's' : ''} • ${importedRecords.toLocaleString('es-ES')} registros importados`);
        setStep('complete');
        // NO cerrar automáticamente - el usuario debe revisar los errores
      } else {
        // Todo exitoso
        setSuccess(`✅ Importación completa: ${response.data.imported} toma(s) • ${importedRecords.toLocaleString('es-ES')} registros`);
        setStep('complete');
        // Cerrar automáticamente solo si todo fue exitoso
        setTimeout(() => {
          onImportComplete();
          onClose();
        }, 3000);
      }
    } catch (err: any) {
      // Detener la simulación si hay error
      clearInterval(progressInterval);
      setError(err.response?.data?.error || 'Error al importar las tomas');
      setErrorDetails({
        message: err.message,
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        config: {
          url: err.config?.url,
          method: err.config?.method,
          headers: err.config?.headers
        },
        timestamp: new Date().toISOString()
      });
      console.error('Error importing tomas:', err);
      setStep('select');
    } finally {
      setImporting(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Sin fecha';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const getSelectedCount = () => tomas.filter(t => t.selected && !t.exists).length;
  const getAvailableCount = () => tomas.filter(t => !t.exists).length;
  
  const copyErrorDetails = () => {
    if (errorDetails) {
      const details = `Error Report - ${errorDetails.timestamp}\n` +
        `Status: ${errorDetails.status} ${errorDetails.statusText}\n` +
        `Message: ${errorDetails.message}\n` +
        `URL: ${errorDetails.config?.url || 'N/A'}\n` +
        `Method: ${errorDetails.config?.method || 'N/A'}\n` +
        `Response Data: ${JSON.stringify(errorDetails.data, null, 2)}`;
      
      navigator.clipboard.writeText(details);
      alert('Detalles del error copiados al portapapeles');
    }
  };

  // Subtítulo contextual del paso actual (se muestra tanto en modal como inline).
  const stepSubtitle =
    step === 'upload'
      ? 'Seleccione un archivo Excel con las tomas'
      : step === 'select'
        ? `${getSelectedCount()} toma(s) seleccionada(s) para importar`
        : step === 'importing'
          ? 'Importando tomas...'
          : 'Importación completada';

  // Cuerpo compartido (content + footer). En inline va dentro de ConfigSectionShell;
  // en modal va dentro del card ad-hoc con header propio.
  const body = (
    <>
      {inline && <p className="text-sm text-gray-500 mb-4">{stepSubtitle}</p>}
      {/* Content */}
      <div className={inline ? '' : 'p-6 overflow-y-auto max-h-[60vh]'}>
          {/* Error/Success Messages */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-red-800 font-medium">{error}</p>
                  {errorDetails && (
                    <button
                      type="button"
                      onClick={() => setShowErrorDetails(!showErrorDetails)}
                      className="mt-2 text-xs text-red-600 hover:text-red-700 underline"
                    >
                      {showErrorDetails ? 'Ocultar detalles técnicos' : 'Mostrar detalles técnicos'}
                    </button>
                  )}
                </div>
              </div>
              {showErrorDetails && errorDetails && (
                <div className="mt-3 p-3 bg-red-100 rounded">
                  <div className="flex justify-between items-start mb-2">
                    <span className="text-xs font-bold text-red-700">Detalles Técnicos</span>
                    <button
                      type="button"
                      onClick={copyErrorDetails}
                      className="flex items-center gap-1 px-2 py-1 text-xs bg-white hover:bg-gray-50 rounded border border-red-300"
                    >
                      <Copy className="w-3 h-3" />
                      Copiar
                    </button>
                  </div>
                  <div className="space-y-1 text-xs font-mono overflow-x-auto">
                    <p><strong>Timestamp:</strong> {errorDetails.timestamp}</p>
                    <p><strong>Status:</strong> {errorDetails.status} {errorDetails.statusText}</p>
                    <p><strong>Message:</strong> {errorDetails.message}</p>
                    {errorDetails.config?.url && (
                      <p><strong>URL:</strong> {errorDetails.config.url}</p>
                    )}
                    {errorDetails.config?.method && (
                      <p><strong>Method:</strong> {errorDetails.config.method}</p>
                    )}
                    {errorDetails.data && (
                      <div>
                        <p><strong>Response Data:</strong></p>
                        <pre className="mt-1 p-2 bg-white rounded overflow-x-auto max-h-40 overflow-y-auto">
                          {JSON.stringify(errorDetails.data, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {success && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-green-800">{success}</p>
              </div>
            </div>
          )}

          {/* Upload Step */}
          {step === 'upload' && (
            <div className="space-y-4">
              <div 
                className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors"
                onDrop={(e) => {
                  e.preventDefault();
                  const droppedFile = e.dataTransfer.files[0];
                  if (droppedFile) {
                    setFile(droppedFile);
                  }
                }}
                onDragOver={(e) => e.preventDefault()}
              >
                <FileSpreadsheet className="mx-auto h-12 w-12 text-gray-400" />
                <p className="mt-2 text-sm font-medium text-gray-900">
                  {file ? file.name : 'Arrastre un archivo Excel aquí'}
                </p>
                <p className="text-sm text-gray-500">o</p>
                <label className="mt-2 inline-block">
                  <input
                    type="file"
                    className="hidden"
                    accept=".xlsx,.xls"
                    onChange={handleFileSelect}
                  />
                  <span className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer inline-block">
                    Seleccionar archivo
                  </span>
                </label>
              </div>

              {file && (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <FileSpreadsheet className="w-8 h-8 text-blue-600" />
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{file.name}</p>
                      <p className="text-sm text-gray-500">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Select Step */}
          {step === 'select' && (
            <div className="space-y-3">
              {tomas.length === 0 ? (
                <div className="text-center py-8">
                  <Package className="mx-auto h-12 w-12 text-gray-400" />
                  <p className="mt-2 text-gray-600">No se encontraron tomas en el archivo</p>
                </div>
              ) : (
                <>
                  <div className="flex justify-between items-start mb-4">
                    <div className="text-sm text-gray-600">
                      <p>✓ Tomas disponibles para importar (no existen en la base)</p>
                      <p>✗ Tomas ya existentes (no se pueden reimportar)</p>
                      <p className="mt-2 font-medium text-gray-700">
                        {getSelectedCount()} de {getAvailableCount()} tomas seleccionadas
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={handleSelectAll}
                        className="px-3 py-1 text-sm text-blue-600 hover:text-blue-700 border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors"
                        disabled={getAvailableCount() === 0}
                      >
                        Seleccionar todas
                      </button>
                      <button
                        type="button"
                        onClick={handleDeselectAll}
                        className="px-3 py-1 text-sm text-gray-600 hover:text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                        disabled={getSelectedCount() === 0}
                      >
                        Deseleccionar todas
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {tomas.map((toma, index) => (
                      <div
                        key={toma.tomaNumber}
                        className={`p-4 border rounded-lg ${
                          toma.exists 
                            ? 'bg-gray-50 border-gray-200 opacity-60' 
                            : 'bg-white border-gray-300 hover:border-blue-400'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={toma.selected}
                            onChange={() => handleTomaToggle(index)}
                            disabled={toma.exists}
                            className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500 disabled:opacity-50"
                          />
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <h4 className="font-medium text-gray-900">
                                Toma {toma.tomaNumber}
                                {toma.exists && (
                                  <span className="ml-2 text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded">
                                    Ya existe
                                  </span>
                                )}
                              </h4>
                              <div className="flex items-center gap-4 text-sm text-gray-600">
                                <div className="flex items-center gap-1">
                                  <Calendar className="w-4 h-4" />
                                  <span>{formatDate(toma.fecha)}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <Package className="w-4 h-4" />
                                  <span>{toma.recordCount.toLocaleString()} registros</span>
                                </div>
                              </div>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              Tabla: toma_{toma.tomaNumber}_{client.uid}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Importing Step */}
          {step === 'importing' && (
            <div className="space-y-6">
              
              {/* Panel de progreso principal */}
              <div className="space-y-4">
                {/* Panel superior - Progreso de la toma actual */}
                <div className="bg-blue-50 rounded-lg p-5">
                  <div className="mb-3">
                    <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                      <span className="inline-block w-2 h-2 bg-blue-500 rounded-full animate-pulse"></span>
                      {importProgress.currentToma ? `Procesando ${importProgress.currentToma}` : 'Iniciando importación...'}
                    </h4>
                    <p className="text-xs text-gray-600 mt-1">
                      Progreso de registros para esta toma
                    </p>
                  </div>
                  
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">Registros procesados</span>
                      <span className="text-2xl font-bold text-blue-600">
                        {Math.round((importProgress.recordsProcessed / importProgress.totalRecords) * 100)}%
                      </span>
                    </div>
                    
                    <div className="bg-white rounded-full h-4 shadow-inner">
                      <div 
                        className="bg-gradient-to-r from-blue-500 to-blue-600 h-4 rounded-full transition-all duration-500 relative overflow-hidden"
                        style={{ width: `${Math.min((importProgress.recordsProcessed / importProgress.totalRecords) * 100, 100)}%` }}
                      >
                        <div 
                          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                          style={{
                            animation: 'shimmer 2s infinite',
                            backgroundSize: '200% 100%'
                          }}
                        />
                      </div>
                    </div>
                    
                    <div className="flex justify-between mt-2">
                      <span className="text-lg font-semibold text-gray-800">
                        {importProgress.recordsProcessed.toLocaleString('es-ES')}
                      </span>
                      <span className="text-sm text-gray-500">
                        de {importProgress.totalRecords.toLocaleString('es-ES')} registros totales
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* Panel inferior - Progreso general de tomas */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-3">
                    Progreso general de importación
                  </h4>
                  
                  <div className="grid grid-cols-2 gap-4">
                    {/* Contador de tomas */}
                    <div className="bg-white rounded-lg p-3">
                      <p className="text-xs text-gray-500 mb-1">Tomas completadas</p>
                      <p className="text-xl font-bold text-gray-900">
                        {importProgress.current} <span className="text-sm font-normal text-gray-500">de {importProgress.total}</span>
                      </p>
                      <div className="mt-2 bg-gray-200 rounded-full h-1.5">
                        <div 
                          className="bg-green-500 h-1.5 rounded-full transition-all duration-300"
                          style={{ width: `${(importProgress.current / importProgress.total) * 100}%` }}
                        />
                      </div>
                    </div>
                    
                    {/* Tiempo estimado o estadísticas */}
                    <div className="bg-white rounded-lg p-3">
                      <p className="text-xs text-gray-500 mb-1">Estado</p>
                      <p className="text-sm font-medium text-gray-900">
                        {importProgress.current === importProgress.total 
                          ? 'Finalizando...' 
                          : `Toma ${importProgress.current + 1} de ${importProgress.total}`}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {Math.round((importProgress.current / importProgress.total) * 100)}% completado
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Información adicional */}
              <div className="text-center text-sm text-gray-500">
                <p>Este proceso puede tomar varios minutos.</p>
                <p>No cierre esta ventana hasta que se complete.</p>
              </div>
            </div>
          )}

          {/* Complete Step */}
          {step === 'complete' && (
            <div className="py-8">
              {error ? (
                // Completado con errores
                <div className="space-y-4">
                  <div className="text-center">
                    <AlertCircle className="mx-auto h-12 w-12 text-yellow-500" />
                    <p className="mt-4 text-lg font-medium text-gray-900">
                      Importación completada con advertencias
                    </p>
                    {/* Mostrar solo el mensaje de éxito principal */}
                    {success && (
                      <p className="mt-2 text-gray-600">{success}</p>
                    )}
                  </div>
                  
                  {/* Detalles de registros fallidos */}
                  {errorDetails?.summary && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 space-y-4">
                      {/* Resumen principal */}
                      <div>
                        <h4 className="font-semibold text-yellow-900 flex items-center justify-between">
                          <span>Detalles del problema</span>
                          <span className="text-lg">{errorDetails.summary.totalFailed?.toLocaleString('es-ES')} registros</span>
                        </h4>
                        <p className="text-sm text-yellow-800 mt-1">
                          {errorDetails.message}
                        </p>
                      </div>
                      
                      {/* Posibles causas */}
                      {errorDetails.summary.possibleCauses?.length > 0 && (
                        <div className="border-t border-yellow-200 pt-3">
                          <p className="text-sm font-medium text-yellow-900 mb-2">
                            {errorDetails.summary.possibleCauses[0]?.includes('id_catalogo vacío')
                              ? '⚠️ Problema detectado en el archivo Excel:'
                              : 'Posibles causas:'}
                          </p>
                          <ul className="text-xs text-yellow-800 space-y-1">
                            {errorDetails.summary.possibleCauses.map((cause: string, idx: number) => (
                              <li key={idx} className="flex items-start">
                                <span className="mr-2">{cause.includes('id_catalogo vacío') ? '🔴' : '•'}</span>
                                <span className={cause.includes('id_catalogo vacío') ? 'font-semibold' : ''}>{cause}</span>
                              </li>
                            ))}
                          </ul>
                          {errorDetails.summary.possibleCauses[0]?.includes('id_catalogo vacío') && (
                            <div className="mt-2 p-2 bg-yellow-100 rounded text-xs">
                              <p className="font-medium text-yellow-900">💡 Solución:</p>
                              <p className="text-yellow-800 mt-1">
                                Revise el archivo Excel y asegúrese de que la columna <span className="font-mono bg-white px-1">id_catalogo</span> tenga valores válidos (números) en todas las filas de productos.
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Errores comunes encontrados */}
                      {errorDetails.summary.commonErrors?.length > 0 && (
                        <div className="border-t border-yellow-200 pt-3">
                          <p className="text-sm font-medium text-yellow-900 mb-2">Errores detectados:</p>
                          <ul className="text-xs text-yellow-800 space-y-1 font-mono bg-yellow-100 p-2 rounded">
                            {errorDetails.summary.commonErrors.map((error: string, idx: number) => (
                              <li key={idx}>{error}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Detalles de registros fallidos - NUEVO */}
                      {errorDetails.summary.failedRecordsDetail && (
                        <div className="border-t border-yellow-200 pt-3">
                          <p className="text-sm font-medium text-yellow-900 mb-2">
                            Análisis detallado de {errorDetails.summary.failedRecordsDetail.total} registros fallidos:
                          </p>
                          <div className="space-y-2">
                            {errorDetails.summary.failedRecordsDetail.byErrorType.map((errorGroup: any, idx: number) => (
                              <div key={idx} className="bg-yellow-50 border border-yellow-200 rounded p-2">
                                <p className="text-xs font-semibold text-yellow-900 mb-1">
                                  {errorGroup.errorType} ({errorGroup.count} registros)
                                </p>
                                {errorGroup.samples.length > 0 && (
                                  <div className="text-xs text-yellow-700 space-y-1 ml-2">
                                    <p className="font-medium">Ejemplos:</p>
                                    {errorGroup.samples.map((sample: any, sIdx: number) => (
                                      <div key={sIdx} className="bg-white p-1 rounded border border-yellow-100">
                                        <span className="font-mono">ID: {sample.id_catalogo}</span>
                                        {sample.title && <span className="ml-2">- {sample.title}</span>}
                                        {sample.tomaNumber && <span className="ml-2 text-yellow-600">(Toma {sample.tomaNumber})</span>}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Tomas afectadas - más compacto */}
                      {errorDetails.summary.details?.length > 0 && (
                        <div className="border-t border-yellow-200 pt-3">
                          <p className="text-sm font-medium text-yellow-900 mb-2">
                            Tomas afectadas ({errorDetails.summary.tomasAffected}):
                          </p>
                          <div className="text-xs text-yellow-800 bg-yellow-100 p-2 rounded">
                            {errorDetails.summary.details.map((detail: any, idx: number) => (
                              <span key={idx}>
                                Toma {detail.toma} ({detail.failed} reg.)
                                {idx < errorDetails.summary.details.length - 1 && ' • '}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Recomendación */}
                      <div className="bg-yellow-100 rounded p-3 text-xs text-yellow-900">
                        <p className="font-medium mb-1">Acción recomendada:</p>
                        <p>Exporte los logs del servidor para identificar los registros específicos que fallaron.</p>
                        <p className="mt-1">Si el problema persiste, contacte soporte técnico con el código de tiempo: {errorDetails.timestamp}</p>
                      </div>
                    </div>
                  )}
                  
                  <div className="text-center">
                    <p className="text-sm text-gray-600">
                      Por favor revise los detalles antes de cerrar este modal
                    </p>
                  </div>
                </div>
              ) : (
                // Completado exitosamente
                <div className="text-center">
                  <CheckCircle2 className="mx-auto h-12 w-12 text-green-600" />
                  <p className="mt-4 text-lg font-medium text-gray-900">
                    Importación completada exitosamente
                  </p>
                  <p className="mt-2 text-gray-600">
                    {success || 'Las tomas han sido agregadas al cliente'}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

      {/* Footer */}
      <div className={`flex justify-end gap-3 ${inline ? 'pt-4 mt-4 border-t' : 'p-6 border-t'}`}>
        {!inline && (
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            disabled={importing}
          >
            Cancelar
          </button>
        )}
        {step === 'upload' && (
          <button
            onClick={handleAnalyze}
            disabled={!file || loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Analizando...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                Analizar Excel
              </>
            )}
          </button>
        )}
        {step === 'select' && (
          <button
            onClick={handleImport}
            disabled={getSelectedCount() === 0 || importing}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Importar {getSelectedCount()} toma(s)
          </button>
        )}
      </div>
    </>
  );

  // Inline: la sección se dibuja con la carcasa unificada (icon+title = sidebar).
  if (inline) {
    return (
      <ConfigSectionShell icon={Plus} title="Agregar Tomas" clientName={client.display_name || client.name}>
        {body}
      </ConfigSectionShell>
    );
  }

  // Modal: card ad-hoc con header propio (uso desde ClientCard).
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Importar Tomas para {client.display_name || client.name}
            </h2>
            <p className="text-sm text-gray-500 mt-1">{stepSubtitle}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            disabled={importing}
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        {body}
      </div>
    </div>
  );
};

export default ImportTomasModal;
